/**
 * Paginación prev/next + indicador "Página X de Y · total".
 *
 * Usado por AdminTableShell cuando el prop `pagination` está presente.
 * Si el endpoint backend no devuelve `total`, no renderizar paginación — usar
 * footer plano del shell.
 */

import { ADMIN_TOKENS } from './adminTokens';

interface AdminPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  labels?: {
    prev?: string;
    next?: string;
    pageOf?: (page: number, totalPages: number) => string;
    totalCount?: (total: number) => string;
  };
}

const DEFAULT_LABELS: Required<NonNullable<AdminPaginationProps['labels']>> = {
  prev: 'Anterior',
  next: 'Siguiente',
  pageOf: (page, totalPages) => `Página ${page} de ${totalPages}`,
  totalCount: (total) => `${total} registros`,
};

export function AdminPagination({
  page,
  pageSize,
  total,
  onPageChange,
  labels,
}: AdminPaginationProps) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const isFirst = page <= 1;
  const isLast = page >= totalPages;

  const prevStyle = {
    ...ADMIN_TOKENS.pagination.button,
    ...(isFirst ? ADMIN_TOKENS.pagination.buttonDisabled : {}),
  };
  const nextStyle = {
    ...ADMIN_TOKENS.pagination.button,
    ...(isLast ? ADMIN_TOKENS.pagination.buttonDisabled : {}),
  };

  return (
    <div style={ADMIN_TOKENS.pagination.bar}>
      <span>{L.totalCount(total)}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          style={prevStyle}
          onClick={() => !isFirst && onPageChange(page - 1)}
          disabled={isFirst}
          aria-label={L.prev}
        >
          {L.prev}
        </button>
        <span style={{ minWidth: 120, textAlign: 'center' }}>{L.pageOf(page, totalPages)}</span>
        <button
          type="button"
          style={nextStyle}
          onClick={() => !isLast && onPageChange(page + 1)}
          disabled={isLast}
          aria-label={L.next}
        >
          {L.next}
        </button>
      </div>
    </div>
  );
}
